import axios from 'axios';
import store from '@/store';
import { Module, ActionTree, MutationTree, GetterTree } from 'vuex';
import { ExporoUser, ProfileState, IdToken } from '@/store/profile/types';
import { RootState } from '@/store/types';
import { identifySentryUser } from '@/plugins/sentry';
import { getIdToken } from '@/utils/Auth';
import parseJWT from '@/utils/parseJWT';
import { gtmTrack, gtmActions } from '@/utils/gtm';

const state: ProfileState = {
  user: undefined,
  idToken: undefined,
};

const mutations: MutationTree<ProfileState> = {
  setUser(state: ProfileState, user: ExporoUser) {
    state.user = user;
  },
  setIdToken(state: ProfileState, idToken: IdToken) {
    state.idToken = idToken;
  },
};

const getters: GetterTree<ProfileState, RootState> = {
  userId: (state: ProfileState): number => state.idToken ? Number(state.idToken.user_id) : null,
  idToken: (state: ProfileState) => state.idToken ? getIdToken() : null,
  fullName(state: ProfileState): string {
    if (!state.user) {
      return '';
    }

    const { naturalUser } = state.user;
    const firstName = naturalUser.firstName;
    const lastName = naturalUser.lastName;

    return `${firstName} ${lastName}`;
  },
  exporoId: (state: ProfileState): string => {
    const { idToken } = state;
    const id = (idToken && idToken.user_id) || '';

    return `${id}`;
  },
};

const actions: ActionTree<ProfileState, RootState> = {
  fetchIdToken({ commit, dispatch }: { commit: Function, dispatch: Function }) {
    const idToken = getIdToken();
    if (idToken) {
      const parsedToken = parseJWT(idToken);
      commit('setIdToken', parsedToken);
    }
  },
  fetchUser({ commit , state}: { commit: Function, state: ProfileState }) {
    store.dispatch('appState/startLoader');

    return axios.get(`${process.env.VUE_APP_EXPORO_USER_API}/user/metaData`)
      .then(({ data }) => {
        commit('setUser', data);

        identifySentryUser(data, state.idToken);

        // Add tracking action to Google tag Manager dataLayer
        const trackData = { userId: state.idToken.user_id };
        gtmTrack(gtmActions.USER_AUTH_SUCCESSFUL, trackData);

        store.dispatch('appState/stopLoader');

        return Promise.resolve(data);
      })
      .catch(error => {
        store.dispatch('appState/stopLoader');

        commit('setUser', null);

        return Promise.reject(error.response);
      });
  },
  signOut() {
    const request = (path: string, params: any, method: string='post') => {
      const form = document.createElement('form');
      form.method = method;
      form.action = path;
      for (const key in params) {
        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = key;
        hiddenField.value = params[key];
        form.appendChild(hiddenField);
      }
      document.body.appendChild(form);
      form.submit();
    };
    const payload = {
      redirectURL: `${window.location.origin}${window.location.pathname}`,
    };
    
    return request(`${process.env.VUE_APP_EXPORO_USER_API}/user/session/flush`, payload);
  },
};

const namespaced: boolean = true;

export const profile: Module<ProfileState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations,
};
